import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader, Badge, Button } from '@/components/ui/primitives';
import { Score } from '@/components/Score';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { CreateDialog } from '@/components/CreateDialog';
import { fetchLeads, createLead } from '@/data/sources';
import { useAuth } from '@/auth/AuthContext';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { formatRelative } from '@/lib/utils';
import type { Lead, LeadStatus } from '@/types';

/* /sales/leads — the Leads table. Demonstrates the evidence-first pattern:
 * the score cell is a <Score> (never a bare number) whose affordance opens the
 * Evidence Drawer. Data comes through the gated source (mock or live API). */

const statusTone: Record<LeadStatus, Parameters<typeof Badge>[0]['tone']> = {
  DISCOVERED: 'neutral',
  ENRICHED: 'neutral',
  QUEUED: 'compliance',
  CONTACTED: 'compliance',
  REPLIED: 'success',
  PROMOTED: 'ai',
  SUPPRESSED: 'danger',
};

function LeadRow({ lead }: { lead: Lead }) {
  return (
    <tr className="group border-b border-border-subtle last:border-0 hover:bg-hover">
      <td className="px-3 py-2.5">
        <Link to={`/sales/leads/${lead.id}`} className="block">
          <div className="text-sm font-medium text-text-primary group-hover:text-engine">
            {lead.business_name}
          </div>
          <div className="text-xs text-text-tertiary">
            {[lead.practitioner, lead.modality].filter(Boolean).join(' · ')}
          </div>
        </Link>
      </td>
      <td className="px-3 py-2.5 text-sm text-text-secondary">{lead.location}</td>
      <td className="px-3 py-2.5">
        <Badge tone={statusTone[lead.status]}>{lead.status}</Badge>
      </td>
      {/* evidence-first score cell — an unscored lead says so, never fakes 0 */}
      <td className="px-3 py-2.5">
        {lead.fit_score !== null ? (
          <Score value={lead.fit_score} outOf={100} evidence={lead.fit_evidence} label={`${lead.business_name} fit`} />
        ) : (
          <span className="text-xs text-text-tertiary">Not scored yet</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-sm text-text-secondary">{lead.next_action}</td>
      <td className="px-3 py-2.5 text-right text-xs text-text-tertiary" data-tabular>
        {formatRelative(lead.updated_at)}
      </td>
    </tr>
  );
}

export function LeadsPage() {
  const { activeWorkspace } = useAuth();
  const { active } = useActiveDomain();
  const canCreate = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Keyed by the active domain's projectId so switching "Viewing" invalidates
  // and refetches — no stale previous-domain leads ever remain onscreen.
  const { data: leads, isPending, error, refetch } = useQuery({
    queryKey: ['leads', active?.projectId ?? 'all'],
    queryFn: () => fetchLeads(active?.projectId),
  });

  const handleCreate = async (values: Record<string, string>) => {
    // createLead throws LeadCreateError (with a friendly message) on
    // duplicate/suppressed/error — the dialog renders it inline as guidance.
    const { lead_ref } = await createLead({
      business_name: values.business_name,
      business_email: values.business_email,
      website: values.website,
      city_region: values.city_region,
      modality: values.modality,
      practitioner_name: values.practitioner_name,
    });
    setDialogOpen(false);
    setNotice(lead_ref ? `Lead added — reference ${lead_ref}.` : 'Lead added.');
    await queryClient.invalidateQueries({ queryKey: ['leads'] });
  };

  return (
    <div>
      <PageHeader
        title="Leads"
        subtitle="Evidence-backed prospect intelligence. Click any score to see why."
        actions={
          canCreate ? (
            <Button variant="primary" onClick={() => setDialogOpen(true)}>
              ＋ Add lead
            </Button>
          ) : null
        }
      />

      {notice && (
        <div className="mb-4 rounded-md border border-success/20 bg-success/10 px-3 py-2 text-sm text-success">
          {notice}
        </div>
      )}

      {isPending ? (
        <LoadingState rows={5} />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : leads.length === 0 ? (
        <EmptyState
          title={active ? `No leads yet for ${active.hostname}` : 'No leads yet'}
          teach={
            active
              ? `No leads match ${active.hostname}, the domain you're viewing. Leads from other domains are never shown here — switch the domain in the top bar, or add a lead for this one.`
              : 'Leads appear here as the discovery worker finds and enriches businesses that fit your campaigns — each one arrives with an evidence-backed fit score.'
          }
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse">
              <thead>
                <tr className="border-b border-border-subtle bg-inset/50 text-left text-[11px] uppercase tracking-wide text-text-tertiary">
                  <th className="px-3 py-2 font-semibold">Business</th>
                  <th className="px-3 py-2 font-semibold">Location</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold">Fit score</th>
                  <th className="px-3 py-2 font-semibold">Next action</th>
                  <th className="px-3 py-2 text-right font-semibold">Updated</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <LeadRow key={l.id} lead={l} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="mt-3 text-[11px] text-text-tertiary">
        Compliance rails (disclosure · unsubscribe · suppression · auto-pause) are enforced server-side and
        surfaced, never hidden. Every score carries evidence; every field is operator-editable and audit-logged.
      </p>

      {canCreate && (
        <CreateDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          title="Add lead"
          description="Add a business to your pipeline. It arrives ready to enrich and score."
          submitLabel="Add lead"
          onSubmit={handleCreate}
          fields={[
            { name: 'business_name', label: 'Business name', placeholder: 'Studio name' },
            {
              name: 'business_email',
              label: 'Email',
              type: 'email',
              placeholder: 'hello@studio.com',
              hint: 'If provided, must be a valid email address.',
            },
            { name: 'website', label: 'Website', type: 'url', placeholder: 'https://studio.com' },
            { name: 'city_region', label: 'City / region', placeholder: 'Cardiff' },
            { name: 'modality', label: 'Modality', placeholder: 'Pilates' },
            { name: 'practitioner_name', label: 'Practitioner name', placeholder: 'Full name' },
          ]}
        />
      )}
    </div>
  );
}
